/**
 * The German Kendo texts, keyed by Kendo's own l10n key (#178 slice E).
 *
 * The keys are `<L10N_PREFIX>.<message input>` — `kendo.grid.noRecords`,
 * `kendo.datepicker.today`. They are not guesswork: `ng extract-i18n` compiles Kendo's own
 * templates and writes each key into `messages.xlf` as the unit's `meaning` note, beside the
 * English default it is replacing. That extraction is where this list came from.
 *
 * **Scoped to the components this app renders**, which is what decision 4 asked for. The grid's
 * grouping, column chooser, column locking, Excel export, detail rows, row drag, external editing
 * and AI-assistant messages are left out, along with the time picker, the date-range popup and the
 * tree-flavoured dropdowns — none of them is reachable here, and inventing German for a feature
 * nobody can open is wording that cannot be checked. An uncovered key falls back to Kendo's
 * English default, so adding a feature later degrades gracefully rather than breaking.
 */
export const KENDO_MESSAGES_DE: Readonly<Record<string, string>> = Object.freeze({
  // ---- Grid: paging (#648 covered these eight first; the rest complete the pager) ----
  'kendo.grid.pagerPage': 'Seite',
  'kendo.grid.pagerOf': 'von',
  'kendo.grid.pagerItems': 'Einträgen',
  'kendo.grid.pagerItemsPerPage': 'Einträge pro Seite',
  'kendo.grid.pagerFirstPage': 'Zur ersten Seite',
  'kendo.grid.pagerPreviousPage': 'Zur vorherigen Seite',
  'kendo.grid.pagerNextPage': 'Zur nächsten Seite',
  'kendo.grid.pagerLastPage': 'Zur letzten Seite',
  'kendo.grid.pagerInputLabel': 'Seitenzahl eingeben',
  'kendo.grid.pagerPageNumberInputTitle': 'Seitenzahl',
  'kendo.grid.pagerSelectPage': 'Seite auswählen',

  // The standalone pager the grid renders internally reads its own prefix.
  'kendo.pager.page': 'Seite',
  'kendo.pager.of': 'von',
  'kendo.pager.items': 'Einträgen',
  'kendo.pager.itemsPerPage': 'Einträge pro Seite',
  'kendo.pager.firstPage': 'Zur ersten Seite',
  'kendo.pager.previousPage': 'Zur vorherigen Seite',
  'kendo.pager.nextPage': 'Zur nächsten Seite',
  'kendo.pager.lastPage': 'Zur letzten Seite',
  'kendo.pager.inputLabel': 'Seitenzahl eingeben',
  'kendo.pager.pageNumberInputTitle': 'Seitenzahl',
  'kendo.pager.selectPage': 'Seite auswählen',

  // ---- Grid: the pager's own accessible name ----
  // #191: reachable — ten grids are `[pageable]="true"`, and this is what a screen reader reads
  // for the pager landmark. It was missing because `extract-i18n` writes an EMPTY source for it:
  // Kendo composes it at runtime from `'Page navigation, page {currentPage} of {totalPages}'`,
  // so the placeholders have to be carried over verbatim or the number lands nowhere.
  'kendo.grid.pagerLabel': 'Seitennavigation, Seite {currentPage} von {totalPages}',
  'kendo.pager.ariaLabel': 'Seitennavigation, Seite {currentPage} von {totalPages}',

  // ---- Grid: the states every list shows ----
  'kendo.grid.noRecords': 'Keine Einträge vorhanden.',
  'kendo.grid.loading': 'Wird geladen',
  'kendo.grid.gridLabel': 'Datentabelle',
  'kendo.grid.columns': 'Spalten',
  'kendo.grid.setColumnPosition': 'Spaltenposition festlegen',
  'kendo.grid.selectAllCheckboxLabel': 'Alle Zeilen auswählen',
  'kendo.grid.selectionCheckboxLabel': 'Zeile auswählen',

  // ---- Grid: sorting ----
  'kendo.grid.sortable': 'Sortierbar',
  'kendo.grid.sortAscending': 'Aufsteigend sortieren',
  'kendo.grid.sortDescending': 'Absteigend sortieren',
  'kendo.grid.sortedAscending': 'Aufsteigend sortiert',
  'kendo.grid.sortedDescending': 'Absteigend sortiert',
  'kendo.grid.sortedDefault': 'Nicht sortiert',
  'kendo.grid.sortClearButton': 'Sortierung aufheben',

  // ---- Grid: filtering ----
  // #191: **not reachable today.** No grid in this app sets `[filterable]`; the one
  // `[filterable]="true"` in the templates is on a combobox. These stay because they are correct
  // German for the day a grid turns filtering on — an uncovered key falls back to English, a
  // covered one does not — but nobody has seen them, so treat their wording as unreviewed.
  'kendo.grid.filter': 'Filter',
  'kendo.grid.filterFilterButton': 'Filtern',
  'kendo.grid.filterClearButton': 'Zurücksetzen',
  'kendo.grid.filterClearAllButton': 'Alle Filter zurücksetzen',
  'kendo.grid.filterAndLogic': 'Und',
  'kendo.grid.filterOrLogic': 'Oder',
  'kendo.grid.filterEqOperator': 'Ist gleich',
  'kendo.grid.filterNotEqOperator': 'Ist ungleich',
  'kendo.grid.filterContainsOperator': 'Enthält',
  'kendo.grid.filterNotContainsOperator': 'Enthält nicht',
  'kendo.grid.filterStartsWithOperator': 'Beginnt mit',
  'kendo.grid.filterEndsWithOperator': 'Endet mit',
  'kendo.grid.filterIsEmptyOperator': 'Ist leer',
  'kendo.grid.filterIsNotEmptyOperator': 'Ist nicht leer',
  'kendo.grid.filterIsNullOperator': 'Ist nicht gesetzt',
  'kendo.grid.filterIsNotNullOperator': 'Ist gesetzt',
  'kendo.grid.filterGtOperator': 'Ist größer als',
  'kendo.grid.filterGteOperator': 'Ist größer oder gleich',
  'kendo.grid.filterLtOperator': 'Ist kleiner als',
  'kendo.grid.filterLteOperator': 'Ist kleiner oder gleich',
  'kendo.grid.filterAfterOperator': 'Liegt nach',
  'kendo.grid.filterAfterOrEqualOperator': 'Liegt am oder nach',
  'kendo.grid.filterBeforeOperator': 'Liegt vor',
  'kendo.grid.filterBeforeOrEqualOperator': 'Liegt am oder vor',
  'kendo.grid.filterBooleanAll': '(Alle)',
  'kendo.grid.filterIsTrue': 'Ja',
  'kendo.grid.filterIsFalse': 'Nein',
  'kendo.grid.filterDateToday': 'HEUTE',
  'kendo.grid.filterDateToggle': 'Kalender anzeigen',
  'kendo.grid.filterNumericDecrement': 'Wert verringern',
  'kendo.grid.filterNumericIncrement': 'Wert erhöhen',

  // ---- Dropdowns ----
  'kendo.dropdownlist.noDataText': 'Keine Einträge gefunden',
  'kendo.dropdownlist.filterInputLabel': 'Filter',
  'kendo.dropdownlist.popupLabel': 'Auswahlliste',
  'kendo.dropdownlist.selectButtonText': 'Auswählen',
  'kendo.dropdownlist.adaptiveCloseButtonTitle': 'Schließen',

  'kendo.combobox.noDataText': 'Keine Einträge gefunden',
  'kendo.combobox.clearTitle': 'Leeren',
  'kendo.combobox.popupLabel': 'Auswahlliste',
  'kendo.combobox.selectButtonText': 'Auswählen',
  'kendo.combobox.adaptiveCloseButtonTitle': 'Schließen',

  'kendo.multiselect.noDataText': 'Keine Einträge gefunden',
  'kendo.multiselect.clearTitle': 'Leeren',
  'kendo.multiselect.popupLabel': 'Auswahlliste',
  'kendo.multiselect.adaptiveCloseButtonTitle': 'Schließen',

  // ---- Date picker, and the calendar and date input it embeds ----
  'kendo.datepicker.today': 'Heute',
  'kendo.datepicker.toggle': 'Kalender anzeigen',
  'kendo.datepicker.clearTitle': 'Leeren',
  'kendo.datepicker.prevButtonTitle': 'Zur vorherigen Ansicht',
  'kendo.datepicker.nextButtonTitle': 'Zur nächsten Ansicht',
  'kendo.datepicker.parentViewButtonTitle': 'Zur übergeordneten Ansicht',
  'kendo.datepicker.adaptiveCloseButtonTitle': 'Schließen',

  'kendo.calendar.today': 'Heute',
  'kendo.calendar.prevButtonTitle': 'Zur vorherigen Ansicht',
  'kendo.calendar.nextButtonTitle': 'Zur nächsten Ansicht',
  'kendo.calendar.parentViewButtonTitle': 'Zur übergeordneten Ansicht',

  'kendo.dateinput.clearTitle': 'Leeren',
  'kendo.dateinput.increment': 'Wert erhöhen',
  'kendo.dateinput.decrement': 'Wert verringern',

  // ---- File select, and the upload messages it shares ----
  'kendo.fileselect.select': 'Dateien auswählen …',
  'kendo.fileselect.dropFilesHere': 'Dateien zum Auswählen hierher ziehen',
  'kendo.fileselect.remove': 'Entfernen',
  'kendo.fileselect.invalidFileExtension': 'Dieser Dateityp ist nicht erlaubt.',
  'kendo.fileselect.invalidMaxFileSize': 'Die Datei ist zu groß.',
  'kendo.fileselect.invalidMinFileSize': 'Die Datei ist zu klein.',

  'kendo.upload.select': 'Dateien auswählen …',
  'kendo.upload.dropFilesHere': 'Dateien zum Hochladen hierher ziehen',
  'kendo.upload.remove': 'Entfernen',
  'kendo.upload.cancel': 'Abbrechen',
  'kendo.upload.retry': 'Erneut versuchen',
  'kendo.upload.pause': 'Pausieren',
  'kendo.upload.resume': 'Fortsetzen',
  'kendo.upload.clearSelectedFiles': 'Leeren',
  'kendo.upload.uploadSelectedFiles': 'Hochladen',
  'kendo.upload.headerStatusUploading': 'Wird hochgeladen …',
  'kendo.upload.headerStatusUploaded': 'Fertig',
  'kendo.upload.headerStatusPaused': 'Pausiert',
  'kendo.upload.fileStatusUploaded': 'Datei wurde hochgeladen.',
  'kendo.upload.fileStatusFailed': 'Datei konnte nicht hochgeladen werden.',
  'kendo.upload.filesBatchStatus': 'Dateien',
  'kendo.upload.filesBatchStatusUploaded': 'Dateien wurden hochgeladen.',
  'kendo.upload.filesBatchStatusFailed': 'Dateien konnten nicht hochgeladen werden.',
  'kendo.upload.invalidFileExtension': 'Dieser Dateityp ist nicht erlaubt.',
  'kendo.upload.invalidMaxFileSize': 'Die Datei ist zu groß.',
  'kendo.upload.invalidMinFileSize': 'Die Datei ist zu klein.',

  'kendo.uploaddropzone.externalDropFilesHere': 'Dateien zum Hochladen hierher ziehen',

  // ---- Editor (seller brand info) ----
  'kendo.editor.bold': 'Fett',
  'kendo.editor.italic': 'Kursiv',
  'kendo.editor.underline': 'Unterstrichen',
  'kendo.editor.strikethrough': 'Durchgestrichen',
  'kendo.editor.subscript': 'Tiefgestellt',
  'kendo.editor.superscript': 'Hochgestellt',
  'kendo.editor.alignLeft': 'Text links ausrichten',
  'kendo.editor.alignCenter': 'Text zentrieren',
  'kendo.editor.alignRight': 'Text rechts ausrichten',
  'kendo.editor.alignJustify': 'Blocksatz',
  'kendo.editor.indent': 'Einzug vergrößern',
  'kendo.editor.outdent': 'Einzug verkleinern',
  // The inline override this replaced labelled BOTH list buttons „Aufzählungszeichen"; an
  // ordered list is not that, so the two are told apart here.
  'kendo.editor.insertUnorderedList': 'Aufzählung einfügen',
  'kendo.editor.insertOrderedList': 'Nummerierte Liste einfügen',
  'kendo.editor.blockquote': 'Zitat',
  'kendo.editor.format': 'Format',
  'kendo.editor.fontFamily': 'Schriftart wählen',
  'kendo.editor.fontSize': 'Zeichengröße',
  'kendo.editor.foreColor': 'Vordergrundfarbe',
  'kendo.editor.backColor': 'Hintergrundfarbe',
  'kendo.editor.cleanFormatting': 'Formatierung entfernen',
  'kendo.editor.undo': 'Rückgängig',
  'kendo.editor.redo': 'Wiederholen',
  'kendo.editor.selectAll': 'Alles auswählen',
  'kendo.editor.print': 'Drucken',
  'kendo.editor.viewSource': 'Quelltext anzeigen',
  'kendo.editor.createLink': 'Hyperlink erstellen',
  'kendo.editor.unlink': 'Hyperlink entfernen',
  'kendo.editor.linkText': 'Text',
  'kendo.editor.linkTitle': 'Titel',
  'kendo.editor.linkWebAddress': 'Webadresse',
  'kendo.editor.linkOpenInNewWindow': 'Link in neuem Fenster öffnen',
  'kendo.editor.insertImage': 'Bild einfügen',
  'kendo.editor.imageWebAddress': 'Webadresse',
  'kendo.editor.imageAltText': 'Alternativtext',
  'kendo.editor.imageWidth': 'Breite (px)',
  'kendo.editor.imageHeight': 'Höhe (px)',
  'kendo.editor.insertFile': 'Datei einfügen',
  'kendo.editor.fileText': 'Text',
  'kendo.editor.fileTitle': 'Titel',
  'kendo.editor.fileWebAddress': 'Webadresse',
  'kendo.editor.insertTable': 'Tabelle einfügen',
  'kendo.editor.insertTableHint': 'Tabelle mit {rows} {x} {columns} erstellen',
  'kendo.editor.addColumnBefore': 'Spalte davor einfügen',
  'kendo.editor.addColumnAfter': 'Spalte danach einfügen',
  'kendo.editor.addRowBefore': 'Zeile darüber einfügen',
  'kendo.editor.addRowAfter': 'Zeile darunter einfügen',
  'kendo.editor.deleteColumn': 'Spalte löschen',
  'kendo.editor.deleteRow': 'Zeile löschen',
  'kendo.editor.deleteTable': 'Tabelle löschen',
  'kendo.editor.mergeCells': 'Zellen verbinden',
  'kendo.editor.splitCell': 'Zelle teilen',
  'kendo.editor.dialogApply': 'Übernehmen',
  'kendo.editor.dialogCancel': 'Abbrechen',
  'kendo.editor.dialogInsert': 'Einfügen',
  'kendo.editor.dialogUpdate': 'Aktualisieren',

  // ---- Toolbar (the editor's) and tab strip ----
  'kendo.toolbar.moreToolsTitle': 'Weitere Werkzeuge',
  'kendo.toolbar.nextToolButton': 'Nach rechts scrollen',
  'kendo.toolbar.previousToolButton': 'Nach links scrollen',

  'kendo.tabstrip.closeTitle': 'Schließen',
  'kendo.tabstrip.nextTabButton': 'Nächstes Register',
  'kendo.tabstrip.previousTabButton': 'Vorheriges Register',
});
